import { Platform, Alert, Linking } from 'react-native';
import * as WebBrowser from 'expo-web-browser';

/**
 * Cross-platform helpers for common browser/native API differences.
 *
 * Use these instead of `window.*`, `navigator.*`, `alert`, `confirm`, or `prompt` directly,
 * so the same code path works on both web and native (iOS/Android).
 */

type OpenLinkOptions = {
  /** When true (default on web), open in a new tab. Ignored on native. */
  newTab?: boolean;
  /** When true on native, prefer in-app WebBrowser; otherwise hand off to OS via Linking. */
  inAppBrowser?: boolean;
};

/**
 * Open an external URL.
 * - Web: window.open (or window.location.href if newTab is false)
 * - Native: WebBrowser.openBrowserAsync (in-app) or Linking.openURL (system)
 */
export const openExternalLink = async (
  url: string,
  options: OpenLinkOptions = {}
): Promise<void> => {
  const { newTab = true, inAppBrowser = true } = options;

  if (!url) return;

  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return;
    if (newTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }
    return;
  }

  try {
    if (inAppBrowser) {
      await WebBrowser.openBrowserAsync(url);
    } else {
      await Linking.openURL(url);
    }
  } catch (error) {
    console.error('Failed to open link:', error);
  }
};

/**
 * Open a tel: link to dial a phone number.
 * - Web: sets window.location.href to tel:
 * - Native: Linking.openURL('tel:...')
 */
export const openTelLink = async (rawPhone: string): Promise<void> => {
  const phone = rawPhone.replace(/[^\d+]/g, '');
  const url = `tel:${phone}`;

  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return;
    window.location.href = url;
    return;
  }

  try {
    await Linking.openURL(url);
  } catch (error) {
    console.error('Failed to open phone link:', error);
  }
};

/**
 * Open an sms: link to compose a text message.
 */
export const openSmsLink = async (rawPhone: string, body?: string): Promise<void> => {
  const phone = rawPhone.replace(/[^\d+]/g, '');
  const query = body ? `?body=${encodeURIComponent(body)}` : '';
  const url = `sms:${phone}${query}`;

  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return;
    window.location.href = url;
    return;
  }

  try {
    await Linking.openURL(url);
  } catch (error) {
    console.error('Failed to open SMS link:', error);
  }
};

/**
 * Open a mailto: link to compose an email.
 */
export const openMailLink = async (
  email: string,
  options: { subject?: string; body?: string } = {}
): Promise<void> => {
  const params: string[] = [];
  if (options.subject) params.push(`subject=${encodeURIComponent(options.subject)}`);
  if (options.body) params.push(`body=${encodeURIComponent(options.body)}`);
  const query = params.length ? `?${params.join('&')}` : '';
  const url = `mailto:${email}${query}`;

  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return;
    window.location.href = url;
    return;
  }

  try {
    await Linking.openURL(url);
  } catch (error) {
    console.error('Failed to open email link:', error);
  }
};

/**
 * Copy a string to the clipboard.
 * - Web: navigator.clipboard.writeText (with execCommand fallback)
 * - Native: best-effort no-op when no clipboard library is installed.
 *   Returns false so callers can show a manual fallback if needed.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (Platform.OS === 'web') {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        console.error('Clipboard write failed:', error);
      }
    }

    if (typeof document !== 'undefined') {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
      } catch (error) {
        console.error('Clipboard fallback failed:', error);
      }
    }

    return false;
  }

  // Native: no clipboard library installed in this project. Caller can show a manual
  // value via showAlert if desired.
  return false;
};

/**
 * Show a simple alert dialog.
 * - Web: window.alert
 * - Native: Alert.alert
 */
export const showAlert = (message: string, title = 'Notice'): void => {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return;
    window.alert(message);
    return;
  }

  Alert.alert(title, message);
};

type ConfirmOptions = {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

/**
 * Show a confirmation dialog and resolve to true/false.
 * - Web: window.confirm
 * - Native: Alert.alert with Cancel/Confirm buttons
 */
export const showConfirm = (message: string, options: ConfirmOptions = {}): Promise<boolean> => {
  const { title = 'Confirm', confirmLabel = 'OK', cancelLabel = 'Cancel', destructive } = options;

  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return Promise.resolve(false);
    return Promise.resolve(window.confirm(message));
  }

  return new Promise((resolve) => {
    Alert.alert(title, message, [
      { text: cancelLabel, style: 'cancel', onPress: () => resolve(false) },
      {
        text: confirmLabel,
        style: destructive ? 'destructive' : 'default',
        onPress: () => resolve(true),
      },
    ]);
  });
};

/**
 * Get the current origin (e.g. https://app.example.com) on web.
 * Returns an empty string on native; callers should provide a fallback there.
 */
export const getWebOrigin = (): string => {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

/**
 * Build a URL for a path that resolves correctly on both web and native.
 * On native, you may want to fall back to the absolute production URL.
 */
export const buildAppUrl = (path: string, fallbackOrigin?: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const origin = getWebOrigin();
  if (origin) return `${origin}${normalizedPath}`;
  if (fallbackOrigin) return `${fallbackOrigin.replace(/\/$/, '')}${normalizedPath}`;
  return normalizedPath;
};
